/**
 * Backup Codes Display Component (Story 1.7)
 * Displays backup codes with copy and download options.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Button } from "../atoms/Button";

interface BackupCodesDisplayProps {
  codes: string[];
  onConfirm?: () => void;
}

export function BackupCodesDisplay({ codes, onConfirm }: BackupCodesDisplayProps) {
  const { t } = useTranslation("auth");
  const [copied, setCopied] = useState(false);

  const handleCopyAll = async () => {
    const text = codes.join("\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const text = `GlobalClean Backup Codes\n${"=".repeat(30)}\n\nStore these codes securely. Each code can only be used once.\n\n${codes.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\nGenerated: ${new Date().toISOString()}`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "globalclean-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Warning */}
      <div className="flex gap-3 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
        <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-warning-800 dark:text-warning-200">
          <p className="font-medium">{t("backup_save_title")}</p>
          <p className="mt-1 text-warning-700 dark:text-warning-300">
            {t("backup_save_description")}
          </p>
        </div>
      </div>

      {/* Codes grid */}
      <div className="grid grid-cols-2 gap-2 p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg font-mono text-sm">
        {codes.map((code, index) => (
          <div
            key={index}
            className="px-3 py-2 bg-white dark:bg-neutral-800 rounded border border-neutral-200 dark:border-neutral-700 text-center"
          >
            {code}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopyAll}
          leftIcon={
            copied ? (
              <CheckIcon className="w-4 h-4" />
            ) : (
              <ClipboardDocumentIcon className="w-4 h-4" />
            )
          }
          className="flex-1"
        >
          {copied ? t("backup_copied") : t("backup_copy_all")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
          className="flex-1"
        >
          {t("backup_download")}
        </Button>
      </div>

      {/* Confirm button */}
      {onConfirm && (
        <Button variant="primary" onClick={onConfirm} className="w-full">
          {t("backup_saved_confirmation")}
        </Button>
      )}
    </div>
  );
}
